import StripeCheckoutElement from "./StripeCheckoutElement";
import { Button, Card, Text } from "../../components";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { Link } from "react-router-dom";
import type {
  PaymentViewModel,
  SummaryCardModel,
  SummaryItem,
} from "./viewModel";

const stripePromise = (() => {
  const key = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
  return key ? loadStripe(key) : null;
})();

function SummaryRow(item: SummaryItem) {
  return (
    <div key={item.label} className="flex items-center gap-[12px]">
      <div className="w-[30px] h-[30px] flex items-center justify-center rounded-full bg-[#3398F4]">
        <img
          src={item.icon}
          alt={item.label}
          className="w-[18px] h-[18px] object-contain"
        />
      </div>
      <div>
        {item.label ? <Text level={3}>{item.label}</Text> : null}
        {item.value ? <Text>{item.value}</Text> : null}
      </div>
    </div>
  );
}

function SummaryCard({ title, subtitle, items, footer }: SummaryCardModel) {
  return (
    <div>
      {title && !subtitle ? (
        <Text level={4} className="font-[700] mb-[24px]">
          {title}
        </Text>
      ) : null}
      <Card className="p-[0px] overflow-hidden border border-divider">
        {subtitle ? (
          <div className="p-[16px] bg-table-header border-b border-divider">
            <Text level={1}>{title}</Text>
            {subtitle}
          </div>
        ) : null}
        <div className="p-[16px] grid gap-[16px]">
          {items.map((item, index) => (
            <SummaryRow key={index} {...item} />
          ))}
        </div>
        {footer}
      </Card>
    </div>
  );
}

export type PaymentViewProps = PaymentViewModel;

function PaymentView(props: PaymentViewProps) {
  return (
    <div className="mt-[16px]">
      <div className="grid grid-cols-1 gap-[24px] lg:grid-cols-2">
        <div className="space-y-[24px]">
          {props.leftCards.map((card, idx) => (
            <SummaryCard key={idx} {...card} />
          ))}
        </div>

        <Card className="border border-divider bg-table-header space-y-[24px] self-start h-fit">
          <Text level={1} className="font-[700]">
            {props.t("general.billingDetails")}
          </Text>

          <div className="grid gap-[16px]">
            {props.billingItems.map((item, index) => (
              <SummaryRow key={index} {...item} />
            ))}
          </div>
          {stripePromise && props.data?.clientSecret ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: props.data.clientSecret,
              }}
            >
              <StripeCheckoutElement payLabel={props.t("general.pay")} />
            </Elements>
          ) : (
            props.data.stripeCheckOutUrl && (
              <Button className="w-full" color="primary" onClick={props.onPay}>
                {props.t("general.pay")}
              </Button>
            )
          )}
          <Link to={props.returnsAndExchangesUrl} target="_blank">
            <Text
              level={5}
              children={props.t("general.freeReturnsAndExchanges")}
              className="text-text-secondary-light text-center underline mt-[16px] cursor-pointer"
            />
          </Link>
        </Card>
      </div>
    </div>
  );
}

PaymentView.SummaryRow = SummaryRow;

export default PaymentView;
